import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, Save, Eye, EyeOff, GripVertical } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import UploadButton from './UploadButton';

const ICONS = ['Heart', 'GraduationCap', 'HandHeart', 'Users', 'Sparkles', 'BookOpen', 'Award'];
const TONES = ['coral', 'gold'];

const empty = {
  badge: '', badge_text: '', title_html: '', description: '', image: '',
  primary_cta_label: 'Bağış Yap', primary_cta_to: '/bagis', primary_cta_icon: 'Heart',
  secondary_cta_label: '', secondary_cta_to: '',
  tertiary_cta_label: '', tertiary_cta_to: '',
  stat_kicker: '', stat_value: '', stat_progress: 0, stat_note: '', stat_icon: 'Heart', stat_tone: 'coral',
  quote_text: '', quote_author: '',
  pill_value: '', pill_label: '', pill_avatars: false,
  sort_order: 0, published: true,
};

export default function AdminHero() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => api.adminListHeroSlides().then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing };
      if (payload.id) {
        const id = payload.id; delete payload.id; delete payload.created_at; delete payload.updated_at;
        await api.adminUpdateHero(id, payload); toast.success('Güncellendi');
      } else {
        await api.adminCreateHero(payload); toast.success('Eklendi');
      }
      setEditing(null); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Hata');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Slaytı silmek istediğinize emin misiniz?')) return;
    await api.adminDeleteHero(id); toast.success('Silindi'); reload();
  };

  return (
    <div data-testid="admin-hero">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Anasayfa</div>
          <h1 className="font-display text-4xl mt-2 leading-none">Hero Slider</h1>
          <p className="mt-2 text-ink-400 text-sm">{items.length} slayt · sıralama ile yönetin</p>
        </div>
        <button onClick={() => setEditing({ ...empty, sort_order: items.length })} className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Yeni Slayt</button>
      </div>

      <div className="space-y-3">
        {items.map((s, i) => (
          <div key={s.id} className="bg-white rounded-2xl border border-ink-100 p-4 flex items-center gap-4">
            <div className="w-9 h-9 rounded-lg bg-cream-200 grid place-items-center text-ink-400 cursor-grab"><GripVertical className="w-4 h-4" /></div>
            <div className="font-display text-2xl tabular text-ink-300 w-10 text-center">{String(i + 1).padStart(2, '0')}</div>
            {s.image && <img src={s.image} alt="" className="w-20 h-20 rounded-xl object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-ink-600 truncate">{(s.title_html || '').replace(/<[^>]*>/g, '').slice(0, 80) || '(başlıksız)'}</div>
              <div className="text-xs text-ink-400 truncate">{s.description?.slice(0, 100)}</div>
            </div>
            {!s.published && <span className="px-2 py-0.5 text-[10px] rounded-full bg-coral-50 text-coral-300 font-bold uppercase tracking-widest">Taslak</span>}
            <button onClick={() => setEditing(s)} className="bg-ink-600 text-cream rounded-lg px-3 h-9 text-xs font-semibold hover:bg-ink-500 inline-flex items-center gap-1.5"><Edit3 className="w-3.5 h-3.5" /> Düzenle</button>
            <button onClick={() => del(s.id)} className="w-9 h-9 grid place-items-center rounded-lg bg-coral-50 text-coral-300 hover:bg-coral-100"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border border-ink-100 text-ink-400">Henüz slayt yok</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-ink-100 flex items-center justify-between">
              <h2 className="font-display text-2xl">{editing.id ? 'Slayt Düzenle' : 'Yeni Slayt'}</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 grid place-items-center hover:bg-cream-200 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              <Group title="Rozet">
                <Inp label="Rozet (örn: 1992)" v={editing.badge} on={v => setEditing({ ...editing, badge: v })} />
                <Inp label="Rozet metni" v={editing.badge_text} on={v => setEditing({ ...editing, badge_text: v })} />
              </Group>
              <Group title="Görsel" cols="full">
                <div className="flex gap-2">
                  <input value={editing.image} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="https:// veya /uploads/..." className="flex-1 bg-cream-200 border border-ink-100 rounded-xl px-3 h-10 text-sm outline-none" />
                  <UploadButton onUploaded={url => setEditing({ ...editing, image: url })} />
                </div>
                {editing.image && <img src={editing.image} alt="" className="mt-2 max-h-40 rounded-lg border border-ink-100" />}
              </Group>
              <Group title="Başlık (HTML destekler)" cols="full">
                <Txt v={editing.title_html} on={v => setEditing({ ...editing, title_html: v })} rows={3} />
              </Group>
              <Group title="Açıklama" cols="full">
                <Txt v={editing.description} on={v => setEditing({ ...editing, description: v })} rows={3} />
              </Group>

              <Group title="Birincil CTA">
                <Inp label="Etiket" v={editing.primary_cta_label} on={v => setEditing({ ...editing, primary_cta_label: v })} />
                <Inp label="Hedef URL" v={editing.primary_cta_to} on={v => setEditing({ ...editing, primary_cta_to: v })} />
                <Sel label="İkon" v={editing.primary_cta_icon} on={v => setEditing({ ...editing, primary_cta_icon: v })} options={ICONS} />
              </Group>
              <Group title="İkincil CTA">
                <Inp label="Etiket" v={editing.secondary_cta_label} on={v => setEditing({ ...editing, secondary_cta_label: v })} />
                <Inp label="Hedef URL" v={editing.secondary_cta_to} on={v => setEditing({ ...editing, secondary_cta_to: v })} />
              </Group>

              <Group title="Stat Kartı">
                <Inp label="Üst metin" v={editing.stat_kicker} on={v => setEditing({ ...editing, stat_kicker: v })} />
                <Inp label="Değer" v={editing.stat_value} on={v => setEditing({ ...editing, stat_value: v })} />
                <Inp label="Not" v={editing.stat_note} on={v => setEditing({ ...editing, stat_note: v })} />
                <Inp label="İlerleme %" type="number" v={editing.stat_progress} on={v => setEditing({ ...editing, stat_progress: parseInt(v) || 0 })} />
                <Sel label="İkon" v={editing.stat_icon} on={v => setEditing({ ...editing, stat_icon: v })} options={ICONS} />
                <Sel label="Renk tonu" v={editing.stat_tone} on={v => setEditing({ ...editing, stat_tone: v })} options={TONES} />
              </Group>
              <Group title="Bursiyer Alıntı">
                <Inp label="Alıntı" v={editing.quote_text} on={v => setEditing({ ...editing, quote_text: v })} />
                <Inp label="Yazar" v={editing.quote_author} on={v => setEditing({ ...editing, quote_author: v })} />
              </Group>

              <Group title="Pill (alt)">
                <Inp label="Değer" v={editing.pill_value} on={v => setEditing({ ...editing, pill_value: v })} />
                <Inp label="Etiket" v={editing.pill_label} on={v => setEditing({ ...editing, pill_label: v })} />
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!editing.pill_avatars} onChange={e => setEditing({ ...editing, pill_avatars: e.target.checked })} />
                  Avatar göster
                </label>
              </Group>

              <Group title="Yayın">
                <Inp label="Sıra" type="number" v={editing.sort_order} on={v => setEditing({ ...editing, sort_order: parseInt(v) || 0 })} />
                <button
                  onClick={() => setEditing({ ...editing, published: !editing.published })}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-3 h-10 text-sm font-semibold ${editing.published ? 'bg-emerald-50 text-emerald-700' : 'bg-coral-50 text-coral-300'}`}
                >{editing.published ? <><Eye className="w-4 h-4" /> Yayında</> : <><EyeOff className="w-4 h-4" /> Taslak</>}</button>
              </Group>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-ink-100 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 hover:bg-cream-200 text-sm font-semibold">İptal</button>
              <button onClick={save} className="bg-gold text-ink-700 rounded-full px-5 h-11 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Group({ title, children, cols = 'normal' }) {
  return (
    <div className={`bg-cream-200 rounded-xl p-4 ${cols === 'full' ? 'sm:col-span-2' : ''}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-ink-500 font-bold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Inp({ label, v, on, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold">{label}</label>
      <input type={type} value={v ?? ''} onChange={e => on(e.target.value)} className="mt-1 w-full bg-white border border-ink-100 focus:border-ink-500 rounded-lg px-3 h-9 text-sm outline-none" />
    </div>
  );
}
function Txt({ v, on, rows = 3 }) {
  return <textarea rows={rows} value={v ?? ''} onChange={e => on(e.target.value)} className="w-full bg-white border border-ink-100 focus:border-ink-500 rounded-lg px-3 py-2 text-sm outline-none" />;
}
function Sel({ label, v, on, options }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold">{label}</label>
      <select value={v} onChange={e => on(e.target.value)} className="mt-1 w-full bg-white border border-ink-100 rounded-lg px-3 h-9 text-sm outline-none">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
